import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import * as Yup from 'yup'
import { useIsPanelPreview } from '../../dashboard/panelPreviewContext'

// Saves the card's whole value object, never one field (ADR-051). `initial` re-seeds `values`, so the
// caller must memoize it or a fresh reference each render fights typing.
export const useCardAutosave = <T extends Record<string, unknown>>(
  initial: T,
  schema: Yup.AnyObjectSchema,
  save: (values: T) => Promise<void>,
  debounceMs = 1800
) => {
  const preview = useIsPanelPreview()
  const [values, setValues] = useState(initial)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const savedRef = useRef(initial)
  const initialRef = useRef(initial)
  const latestValuesRef = useRef(initial)
  const saveInFlightRef = useRef(false)
  const deferredInitialRef = useRef<T | null>(null)

  useEffect(() => {
    latestValuesRef.current = values
    if (values !== savedRef.current) {
      setStatus((current) => (current === 'saved' ? 'idle' : current))
    }
  }, [values])

  useEffect(() => {
    if (initial !== initialRef.current) {
      initialRef.current = initial
      if (latestValuesRef.current === savedRef.current && !saveInFlightRef.current) {
        savedRef.current = initial
        latestValuesRef.current = initial
        setValues(initial)
      } else {
        deferredInitialRef.current = initial
      }
    }
  }, [initial])

  // The component library mounts these panels for real, so a keystroke in a preview never reaches the record (ADR-049).
  useEffect(() => {
    if (preview || values === savedRef.current) {
      return undefined
    }

    const timer = setTimeout(() => {
      schema
        .validate(values)
        .then(async () => {
          saveInFlightRef.current = true
          setStatus('saving')
          await save(values)
          saveInFlightRef.current = false

          if (latestValuesRef.current !== values) {
            setError(null)
            setStatus('idle')
            return
          }

          const nextValues = deferredInitialRef.current ?? values
          deferredInitialRef.current = null
          savedRef.current = nextValues
          latestValuesRef.current = nextValues
          if (nextValues !== values) {
            setValues(nextValues)
          }
          setError(null)
          setStatus('saved')
        })
        .catch((err: Error) => {
          saveInFlightRef.current = false
          setStatus('idle')
          setError(err.message)
        })
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [values, schema, save, debounceMs, preview])

  return { values, setValues, error, status }
}

export const makeFieldSetter =
  <T extends Record<string, unknown>>(setValues: Dispatch<SetStateAction<T>>) =>
  <K extends keyof T>(key: K) =>
  (value: T[K]) =>
    setValues((current) => ({ ...current, [key]: value }))
